import type { PostMutationOptions, PostPublic } from '@repo/types/posts';

import { mutationOptions, useMutation, useQueryClient, QueryClient } from '@tanstack/react-query';

import { GroupKey } from '#group/keys';

import { PostKey } from './keys';
import { PostService } from './postService';

function deleteGroupPostMutationOptions(queryClient: QueryClient, groupId: number) {
  return mutationOptions({
    mutationFn: (id: number) => PostService.deletePost(id).then((response) => response.data),
    onMutate: async (id: number) => {
      await queryClient.cancelQueries({ queryKey: [PostKey.POSTS] });

      const previousPosts = queryClient.getQueryData<PostPublic[]>([PostKey.POSTS]);

      queryClient.setQueryData([PostKey.POSTS], (prev?: PostPublic[]) => {
        if (!prev) {
          return [];
        }
        return prev.filter((post: PostPublic) => post.id !== id);
      });

      return { previousPosts };
    },
    onError: (_error, _id, context) => {
      if (context?.previousPosts) {
        queryClient.setQueryData<PostPublic[]>([PostKey.POSTS], context.previousPosts);
      }
    },

    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: [PostKey.POSTS] });
      void queryClient.invalidateQueries({
        queryKey: [GroupKey.GROUP_POSTS_INFINITE, groupId],
      });
    },
  });
}

function deletePostMutationOptions(queryClient: QueryClient) {
  return mutationOptions({
    mutationFn: (id: number) => PostService.deletePost(id).then((response) => response.data),
    onMutate: async (id: number) => {
      await queryClient.cancelQueries({ queryKey: [PostKey.POSTS] });

      const previousPosts = queryClient.getQueryData<PostPublic[]>([PostKey.POSTS]);

      queryClient.setQueryData([PostKey.POSTS], (prev?: PostPublic[]) => {
        if (!prev) {
          return [];
        }
        return prev.filter((post: PostPublic) => post.id !== id);
      });

      return { previousPosts };
    },
    onError: (_error, _id, context) => {
      if (context?.previousPosts) {
        queryClient.setQueryData<PostPublic[]>([PostKey.POSTS], context.previousPosts);
      }
    },

    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: [PostKey.POSTS] });
      void queryClient.invalidateQueries({
        queryKey: [PostKey.POSTS_INFINITE],
      });
    },
  });
}

export function useDeletePost(options?: PostMutationOptions) {
  const queryClient = useQueryClient();

  const { groupId } = options || {};
  return useMutation(groupId ? deleteGroupPostMutationOptions(queryClient, groupId) : deletePostMutationOptions(queryClient));
}
